import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";
import { industriesList } from "@/mocks/industriesPageData";

export default function IndustryDetailPage({ slug }: { slug: string }) {
  const industry = industriesList.find((i) => i.slug === slug);

  if (!industry) return null;

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Industries", href: "/industries" },
          { label: industry.name },
        ]}
      />

      <PageHero
        badge={industry.name}
        headline={`${industry.name} Technology Solutions`}
        subtext={industry.desc}
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
        backgroundImage={industry.heroImage}
      />

      {/* Industry Overview */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-[300px] md:h-[400px] object-cover object-top"
                />
              </div>
            </AnimatedSection>
            <div>
              <AnimatedSection delay={100}>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                  {industry.tagline}
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-8">
                  {industry.desc}
                </p>
              </AnimatedSection>

              <AnimatedSection delay={200}>
                <h3 className="text-sm font-bold text-foreground-800 uppercase tracking-wider mb-4">
                  Industry Challenges
                </h3>
                <ul className="space-y-3">
                  {industry.challenges.map((challenge) => (
                    <li key={challenge} className="flex items-start gap-3">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-xs" />
                      </span>
                      <span className="text-sm text-foreground-600">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Our Technology Solutions
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {industry.solutions.map((solution, i) => (
              <AnimatedSection key={solution.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${solution.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{solution.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{solution.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Key Features
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {industry.features.map((feature, i) => (
              <AnimatedSection key={feature.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${feature.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Technology Stack
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {industry.techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className="flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift">
                  {tech.logo ? (
                    <img src={tech.logo} alt={tech.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <span className="w-10 h-10 flex items-center justify-center text-accent-500">
                      <i className={`${tech.icon} text-2xl`} />
                    </span>
                  )}
                  <span className="text-sm font-semibold text-foreground-800">{tech.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Business Benefits
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {industry.benefits.map((benefit, i) => (
              <AnimatedSection key={benefit.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${benefit.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{benefit.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto p-8 md:p-10 rounded-2xl bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/60">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/15 text-accent-400 text-xs font-bold uppercase tracking-wider mb-4">
                Case Study
              </span>
              <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3 font-heading">
                {industry.caseStudy.title}
              </h3>
              <p className="text-sm text-background-400 leading-relaxed mb-6">
                {industry.caseStudy.desc}
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {industry.caseStudy.tech.map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-lg bg-primary-700/50 text-background-300 text-xs font-medium border border-primary-600/30">
                    {t}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-accent-400">
                <i className="ri-check-double-line" />
                <span className="text-sm font-semibold">{industry.caseStudy.results}</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <FAQSection heading={`${industry.name} FAQ`} items={industry.faq} />

      <PageCTA
        heading={`Ready to Transform Your ${industry.name} Business?`}
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}